import { memo } from "react";
import useEvent from "react-use-event-hook";

import { MapLibreAutoPanPopup, MapLibreStationPinMarker } from "@ryde/controls";
import { IPassengerStationsMapInfo, MarkerIconType } from "@ryde/shared";

import { PassengersMapStationPopup } from "./passengers-map-station-popup";

interface IPassengersMapMarkerProps {
  stationMapData: IPassengerStationsMapInfo;
  /**
   * Primitives rather than a pin object: a fresh { type, color } each render would defeat the memo below,
   * and with many stations on the map that means re-rendering every SVG on every hover.
   */
  pinType: MarkerIconType;
  pinColor: string;
  isPopupOpen: boolean;
  distanceAndTimeCacheRefByIds: Parameters<
    typeof PassengersMapStationPopup
  >[0]["distanceAndTimeCacheRefByIds"];
  onClick: (stationMapData: IPassengerStationsMapInfo) => void;
  onHover: (stationMapData: IPassengerStationsMapInfo) => void;
  onLeave: (stationMapData: IPassengerStationsMapInfo) => void;
  onPopupEnter: (stationId: number) => void;
  onPopupLeave: (stationId: number) => void;
  onPopupClose: (stationId: number) => void;
}

/**
 * One station on the passengers map: its pin, the native title shown on hover, and the assigned-passengers
 * popup.
 *
 * The popup's own hover handlers are what keep it open while the pointer travels off the pin and onto it.
 * Leaflet needed those attached to the popup's DOM node after the fact; here they are ordinary JSX props.
 *
 * Memoized, with every callback prop taking the station id, so the parent can hand down one stable handler
 * each instead of a closure per marker.
 */
export const PassengersMapMarker = memo<IPassengersMapMarkerProps>(
  ({
    stationMapData,
    pinType,
    pinColor,
    isPopupOpen,
    distanceAndTimeCacheRefByIds,
    onClick,
    onHover,
    onLeave,
    onPopupEnter,
    onPopupLeave,
    onPopupClose,
  }) => {
    const { stationInfo, assignPassengers } = stationMapData;
    const { id, latitude, longitude, addressName } = stationInfo;

    const handleClick = useEvent((event: { originalEvent: MouseEvent }) => {
      event.originalEvent.stopPropagation();
      onClick(stationMapData);
    });

    const passengerLines = assignPassengers?.length
      ? assignPassengers.map(p => `• ${p.firstName} ${p.lastName}`).join("\n")
      : "";
    const markerTitle = `${addressName} (${id})${
      passengerLines ? `\n${passengerLines}` : ""
    }`.trim();

    return (
      <>
        <MapLibreStationPinMarker
          latitude={latitude}
          longitude={longitude}
          type={pinType}
          color={pinColor}
          onClick={handleClick}
          // The browser's own tooltip would otherwise sit on top of the popup. Leaflet solved this by
          // stripping the attribute off the DOM node when the popup opened and putting it back on close.
          title={isPopupOpen ? undefined : markerTitle}
          actionLabel={markerTitle}
          onMouseEnter={() => onHover(stationMapData)}
          onMouseLeave={() => onLeave(stationMapData)}
        />

        <MapLibreAutoPanPopup
          isOpen={isPopupOpen}
          latitude={latitude}
          longitude={longitude}
          hasCloseButton={false}
          shouldCloseOnMapClick={false}
          onClose={() => onPopupClose(id)}
          onMouseEnter={() => onPopupEnter(id)}
          onMouseLeave={() => onPopupLeave(id)}
        >
          <PassengersMapStationPopup
            stationInfo={stationInfo}
            assignPassengers={assignPassengers}
            distanceAndTimeCacheRefByIds={distanceAndTimeCacheRefByIds}
          />
        </MapLibreAutoPanPopup>
      </>
    );
  },
);

PassengersMapMarker.displayName = "PassengersMapMarker";
